/**
 * Health Fix Center — /admin/system-health
 * Un unico posto per diagnosi + riparazione: credenziali wallet,
 * documenti legali orfani, backup pCloud, sync Salesforce fallite,
 * errori di firma, clienti senza dealer.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wrench, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2, ExternalLink, ShieldAlert, Mail } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { TestDataResetCard } from "../components/admin/TestDataResetCard";

const STATUS_UI = {
  ok: { icon: CheckCircle2, color: "text-emerald-400", chip: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400", label: "OK" },
  warn: { icon: AlertTriangle, color: "text-yellow-400", chip: "bg-yellow-500/10 border-yellow-500/30 text-yellow-400", label: "Attenzione" },
  error: { icon: XCircle, color: "text-lojack-red", chip: "bg-lojack-red/10 border-lojack-red/30 text-lojack-red", label: "Critico" },
};

export default function AdminFixCenterPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null); // "itemId:label"
  const [lastResult, setLastResult] = useState(null); // {itemId, label, payload}
  const [sendingSummary, setSendingSummary] = useState(false);

  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const r = await axios.post(`${API}/admin/fix-center/send-summary`);
      if (r.data.sent) {
        toast.success(`Riepilogo inviato a: ${(r.data.recipients || []).join(", ")}`);
      } else {
        toast.info(`Riepilogo non inviato (${r.data.reason})`);
      }
    } catch {
      toast.error("Invio riepilogo fallito");
    } finally {
      setSendingSummary(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/fix-center/status`);
      setData(r.data);
    } catch {
      toast.error("Impossibile caricare lo stato del Fix Center");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAction = async (item, action) => {
    if (action.link) { navigate(action.link); return; }
    if (action.confirm && !window.confirm(action.confirm)) return;
    const key = `${item.id}:${action.label}`;
    setRunning(key);
    try {
      const r = await axios.post(`${API}${action.endpoint}`);
      setLastResult({ itemId: item.id, label: action.label, payload: r.data });
      toast.success(`${action.label}: completato`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail || `${action.label}: fallito`);
    } finally {
      setRunning(null);
    }
  };

  const isSuperAdmin = user?.role === "super_admin";
  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-black text-white px-4 sm:px-6 lg:px-8 py-8 max-w-7xl mx-auto" data-testid="fix-center-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-lojack-red/15 border border-lojack-red/30 flex items-center justify-center">
              <Wrench size={18} className="text-lojack-red" />
            </span>
            Health Fix Center
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Diagnosi e riparazione della piattaforma in un unico posto. Ogni azione riusa le procedure ufficiali ed è tracciata nell&apos;audit log.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded-lg border bg-emerald-500/10 border-emerald-500/30 text-emerald-400" data-testid="fix-summary-ok">{summary.ok ?? "–"} OK</span>
            <span className="px-2 py-1 rounded-lg border bg-yellow-500/10 border-yellow-500/30 text-yellow-400" data-testid="fix-summary-warn">{summary.warn ?? "–"} warn</span>
            <span className="px-2 py-1 rounded-lg border bg-lojack-red/10 border-lojack-red/30 text-lojack-red" data-testid="fix-summary-error">{summary.error ?? "–"} critici</span>
          </div>
          <button
            onClick={sendSummary}
            disabled={sendingSummary}
            title="Invia subito il riepilogo email agli admin iscritti agli alert tecnici (parte comunque ogni lunedì alle 8:00 se ci sono problemi)"
            className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-gray-700 rounded-xl text-sm hover:bg-white/10 transition disabled:opacity-50"
            data-testid="fix-center-send-summary"
          >
            {sendingSummary ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            Invia riepilogo ora
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-gray-700 rounded-xl text-sm hover:bg-white/10 transition disabled:opacity-50"
            data-testid="fix-center-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Aggiorna
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-gray-600" /></div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        {(data?.items || []).map((item) => {
          const ui = STATUS_UI[item.status] || STATUS_UI.warn;
          const Icon = ui.icon;
          return (
            <div key={item.id} className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid={`fix-item-${item.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-white text-sm font-semibold flex items-center gap-2">
                  <Icon size={16} className={ui.color} />
                  {item.title}
                  {typeof item.count === "number" && item.count > 0 && (
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-gray-700 text-gray-300">{item.count}</span>
                  )}
                </h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ui.chip}`} data-testid={`fix-status-${item.id}`}>{ui.label}</span>
              </div>
              <p className="text-gray-400 text-xs mt-2 leading-relaxed">{item.detail}</p>

              {item.actions?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {item.actions.map((action) => {
                    const key = `${item.id}:${action.label}`;
                    const blocked = action.super_admin && !isSuperAdmin;
                    return (
                      <button
                        key={key}
                        onClick={() => runAction(item, action)}
                        disabled={running === key || blocked}
                        title={blocked ? "Richiede ruolo super admin" : undefined}
                        className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 transition disabled:opacity-40 ${
                          action.variant === "danger"
                            ? "border-lojack-red/40 bg-lojack-red/10 text-lojack-red hover:bg-lojack-red/20"
                            : action.link
                              ? "border-gray-700 text-gray-300 hover:bg-white/5"
                              : "border-lojack-blue-500/40 bg-lojack-blue-500/10 text-lojack-blue-300 hover:bg-lojack-blue-500/20"
                        }`}
                        data-testid={`fix-action-${item.id}-${action.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {running === key ? <Loader2 size={11} className="animate-spin" /> : action.link ? <ExternalLink size={11} /> : blocked ? <ShieldAlert size={11} /> : <Wrench size={11} />}
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {lastResult?.itemId === item.id && (
                <pre className="mt-3 bg-black/60 border border-gray-800 rounded-lg p-2.5 text-[10px] text-gray-400 overflow-x-auto max-h-36" data-testid={`fix-result-${item.id}`}>
                  {JSON.stringify(lastResult.payload, null, 1)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      <TestDataResetCard isSuperAdmin={isSuperAdmin} />
    </div>
  );
}
